export function canUseGis(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "sellsynq.com" ||
    host.endsWith(".sellsynq.com") ||
    host.endsWith(".run.app") ||
    host.endsWith(".google.com")
  );
}

/**
 * BigQuery OAuth access tokens via Google Identity Services (GIS).
 *
 * Why not just Firebase's popup? Firebase `signInWithPopup` can only hand back a
 * Google access token by opening a popup every time, and the token it returns lives
 * ~1h with no way to refresh it quietly. That's the "it keeps asking me to connect
 * for billing" problem.
 *
 * GIS token client solves it: after the user grants the scopes ONCE, we can mint a
 * fresh access token SILENTLY (`prompt: ""`, no popup) for as long as they have an
 * active Google session — which @google.com users always do. So: one consent, then
 * seamless forever.
 *
 * Requirement: the OAuth client below must list the app's origins under
 * "Authorized JavaScript origins" in the Google Cloud console. If it doesn't (or GIS
 * is unreachable), every call here rejects and the caller falls back to the Firebase
 * popup — i.e. today's behavior, no regression.
 */

import { getFirebaseAuth } from "./firebase.ts";

// Public Web OAuth client id (client ids are not secret). Must have the QC + prod
// app origins under Authorized JavaScript origins for the silent flow to work.
const CLIENT_ID =
  import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID ||
  "1037800895337-97de77ttdpvbh91fkdnujvqtr0f4timf.apps.googleusercontent.com";

/** The email of the currently signed-in Sally user, used as the GIS login_hint so the
 * silent token refresh targets the right account. Best-effort — "" if not signed in. */
function currentUserEmail(): string {
  try {
    return getFirebaseAuth().currentUser?.email ?? "";
  } catch {
    return "";
  }
}

const BQ_SCOPES =
  "https://www.googleapis.com/auth/bigquery https://www.googleapis.com/auth/userinfo.email";

interface TokenResponse {
  access_token?: string;
  error?: string;
}
interface TokenClient {
  requestAccessToken: (opts?: { prompt?: string }) => void;
}
interface CodeResponse {
  code?: string;
  error?: string;
}
interface CodeClient {
  requestCode: () => void;
}
interface GoogleOAuth2 {
  initTokenClient: (cfg: {
    client_id: string;
    scope: string;
    // login_hint: which Google account to use. Critical for the SILENT flow — corp
    // users often have several accounts signed in, and without a hint GIS can't pick
    // one quietly, so it fails and the app falls back to a re-consent prompt.
    hint?: string;
    callback: (resp: TokenResponse) => void;
    error_callback?: (err: { type?: string }) => void;
  }) => TokenClient;
  // Authorization-CODE flow (offline access). The popup returns a one-time code we
  // hand to the server, which exchanges it for a long-lived refresh token.
  initCodeClient: (cfg: {
    client_id: string;
    scope: string;
    ux_mode: "popup";
    prompt?: string;
    login_hint?: string;
    callback: (resp: CodeResponse) => void;
    error_callback?: (err: { type?: string }) => void;
  }) => CodeClient;
}
function gis(): GoogleOAuth2 | null {
  const g = (window as unknown as { google?: { accounts?: { oauth2?: GoogleOAuth2 } } }).google;
  return g?.accounts?.oauth2 ?? null;
}

let _load: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (_load) return _load;
  _load = new Promise<void>((resolve, reject) => {
    if (gis()) return resolve();
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Google Identity Services"));
    document.head.appendChild(s);
  });
  return _load;
}

const CALENDAR_SCOPES =
  "https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/userinfo.email";

/** Warm up the GIS script so a later token request can open its popup synchronously
 * inside the user's click (a popup opened after an async script load is blocked). */
export function preloadGis(): void {
  void loadGis().catch(() => {});
}

/** Mint a Google access token for the given scope via GIS. `interactive:true` shows the
 * consent screen (first grant); `interactive:false` is silent (`prompt:""`) and only
 * succeeds if the user already granted and has a live Google session. Rejects on any
 * failure so the caller can fall back. */
async function mintTokenGis(scope: string, interactive: boolean): Promise<string> {
  if (!canUseGis()) {
    throw new Error("GIS origin not authorized on tenant subdomains");
  }
  await loadGis();
  const oauth2 = gis();
  if (!oauth2) throw new Error("GIS unavailable");
  // Pin the request to the currently signed-in Sally account so the silent refresh
  // resolves without prompting even when other Google accounts are logged in.
  const hint = currentUserEmail();
  return new Promise<string>((resolve, reject) => {
    let settled = false;
    const done = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };
    // Guard against a flow that never calls back (e.g. blocked popup).
    const timer = setTimeout(() => done(() => reject(new Error("GIS token timeout"))), 60_000);
    const client = oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope,
      ...(hint ? { hint } : {}),
      callback: (resp) => {
        clearTimeout(timer);
        if (resp.access_token) done(() => resolve(resp.access_token as string));
        else done(() => reject(new Error(resp.error || "no access_token")));
      },
      error_callback: (err) => {
        clearTimeout(timer);
        done(() => reject(new Error(err?.type || "GIS error")));
      },
    });
    client.requestAccessToken({ prompt: interactive ? "consent" : "" });
  });
}

/** Mint a BigQuery access token via GIS (see {@link mintTokenGis}). */
export function getBigQueryTokenGis(interactive: boolean): Promise<string> {
  return mintTokenGis(BQ_SCOPES, interactive);
}

/** Silent best-effort BigQuery token: a fresh token if the user already granted, else ""
 * (never throws, never shows UI). Safe to call from a non-gesture auto-load effect. */
export async function getBigQueryTokenSilent(): Promise<string> {
  try {
    return await getBigQueryTokenGis(false);
  } catch {
    return "";
  }
}

/** Mint a Google Calendar (read-only) access token via GIS. Used by the "Connect Google
 * Calendar" flow so the app can pull the user's upcoming meetings. */
export function getCalendarTokenGis(interactive: boolean): Promise<string> {
  return mintTokenGis(CALENDAR_SCOPES, interactive);
}

/** Silent best-effort Calendar token: a fresh token if already granted, else "". */
export async function getCalendarTokenSilent(): Promise<string> {
  try {
    return await getCalendarTokenGis(false);
  } catch {
    return "";
  }
}

// Drive export needs write access to files the app creates (drive.file — least
// privilege; the app can never see the user's other Drive content).
const DRIVE_SCOPES =
  "https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/userinfo.email";

/**
 * Run the GIS popup authorization-CODE flow for the given scope and resolve the
 * one-time code. The server exchanges it for a long-lived refresh token (offline
 * access), so the user consents ONCE and never gets re-prompted afterwards. Must be
 * called from a user gesture (it opens a popup). Rejects on cancel/failure.
 */
async function getAuthCode(scope: string): Promise<string> {
  if (!canUseGis()) {
    throw new Error("GIS origin not authorized on tenant subdomains");
  }
  await loadGis();
  const oauth2 = gis();
  if (!oauth2) throw new Error("GIS unavailable");
  const hint = currentUserEmail();
  return new Promise<string>((resolve, reject) => {
    let settled = false;
    const done = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };
    const timer = setTimeout(() => done(() => reject(new Error("GIS code timeout"))), 120_000);
    const client = oauth2.initCodeClient({
      client_id: CLIENT_ID,
      scope,
      ux_mode: "popup",
      prompt: "consent",
      ...(hint ? { login_hint: hint } : {}),
      callback: (resp) => {
        clearTimeout(timer);
        if (resp.code) done(() => resolve(resp.code as string));
        else done(() => reject(new Error(resp.error || "no code")));
      },
      error_callback: (err) => {
        clearTimeout(timer);
        done(() => reject(new Error(err?.type || "GIS error")));
      },
    });
    client.requestCode();
  });
}

/** GIS popup code flow for calendar read-only + email → one-time code for the server. */
export function getCalendarAuthCode(): Promise<string> {
  return getAuthCode(CALENDAR_SCOPES);
}

/** GIS popup code flow for Drive (drive.file) + email → one-time code for the server,
 * which exchanges it for a refresh token used by "Export to Drive". */
export function getDriveAuthCode(): Promise<string> {
  return getAuthCode(DRIVE_SCOPES);
}

export const GMAIL_SEND_SCOPES = "https://www.googleapis.com/auth/gmail.send email";

/** GIS popup code flow for Gmail send permission → one-time code for the server,
 * which exchanges it for a refresh token used by transactional emails. */
export function getGmailSenderAuthCode(): Promise<string> {
  return getAuthCode(GMAIL_SEND_SCOPES);
}
